// src/services/calibreBrandBulk.ts
//
// Pure planners for the two brand-filling bulk actions on the calibre listing:
// "Set Brand" (fill a calibre's own `brandId`) and "Set AKA Brands" (fill the
// `brandId` of every unscoped alias on a calibre).
//
// Both actions answer the same question per calibre — "which brand does this
// calibre's manufacturer set point at?" — and both answer it with
// `defaultAkaBrandId` from `calibreClassification.ts`. That resolution is NOT
// repeated here: each planner takes a `targetBrandIdFor(calibreId)` callback, so
// there is still exactly one implementation of the rule, and these functions can
// be tested without a manufacturer catalogue or a brand list.
//
// Kept out of the component (and out of `firebase.ts`) so the bucketing — which
// is where every skip rule lives — is unit-testable. The writers in `firebase.ts`
// take the plan; they make no decisions of their own.

import type { CalibreAliasDetail } from './firebase';

/** The calibre fields the "Set Brand" planner reads. Satisfied by a listing row. */
export interface CalibreBrandPlanInput {
  id: string;
  name?: string;
  brandId?: string;
  brandName?: string;
}

/**
 * What will happen to one selected calibre:
 *  - `write`       — no `brandId` today and a target resolved: fill it.
 *  - `already-set` — `brandId` already equals the target. Nothing to do.
 *  - `conflict`    — `brandId` is set to a DIFFERENT brand. Skipped, never
 *                    overwritten: a bulk action is not the place to reassign a
 *                    brand somebody chose, and the editor is.
 *  - `no-target`   — the manufacturer set resolves to no single brand (a
 *                    multi-brand maker with no `primaryBrandId`, or a maker with
 *                    no brand association at all). Nothing to fill it with.
 */
export type BrandSetOutcome = 'write' | 'already-set' | 'conflict' | 'no-target';

export interface CalibreBrandPlanRow {
  id: string;
  /** Display name, for the conflict list in the confirm modal. */
  name: string;
  currentBrandId?: string;
  targetBrandId?: string;
  outcome: BrandSetOutcome;
}

export interface CalibreBrandPlan {
  rows: CalibreBrandPlanRow[];
  /** The rows that will actually be written. */
  writes: CalibreBrandPlanRow[];
  /** Rows whose `brandId` already matches the target. */
  alreadySet: CalibreBrandPlanRow[];
  /** Rows whose `brandId` names a different brand — listed in the modal. */
  conflicts: CalibreBrandPlanRow[];
  /** Rows no brand could be derived for. */
  noTarget: CalibreBrandPlanRow[];
}

/** Trim-and-empty-to-undefined, the shape every id comparison here wants. */
function id(value?: string | null): string | undefined {
  const trimmed = (value || '').trim();
  return trimmed || undefined;
}

/**
 * Bucket a selection for the "Set Brand" action.
 *
 * Order matters: a calibre with no derivable target is `no-target` regardless of
 * what it currently carries, because without a target there is nothing to call
 * it a conflict WITH.
 */
export function planCalibreBrandUpdates(
  entries: CalibreBrandPlanInput[],
  targetBrandIdFor: (calibreId: string) => string | undefined,
): CalibreBrandPlan {
  const rows: CalibreBrandPlanRow[] = entries.map((entry) => {
    const targetBrandId = id(targetBrandIdFor(entry.id));
    const currentBrandId = id(entry.brandId);
    const name = (entry.name || '').trim() || entry.id;

    let outcome: BrandSetOutcome;
    if (!targetBrandId) outcome = 'no-target';
    else if (!currentBrandId) outcome = 'write';
    else if (currentBrandId === targetBrandId) outcome = 'already-set';
    else outcome = 'conflict';

    return { id: entry.id, name, currentBrandId, targetBrandId, outcome };
  });

  return {
    rows,
    writes: rows.filter((r) => r.outcome === 'write'),
    alreadySet: rows.filter((r) => r.outcome === 'already-set'),
    conflicts: rows.filter((r) => r.outcome === 'conflict'),
    noTarget: rows.filter((r) => r.outcome === 'no-target'),
  };
}

/** The calibre fields the AKA planner reads. Needs a HYDRATED document. */
export interface CalibreAkaPlanInput {
  id: string;
  name?: string;
  alsoKnownAs?: string[] | null;
  aliasDetails?: CalibreAliasDetail[] | null;
}

/**
 * The alias list to treat as authoritative for a calibre.
 *
 * Mirrors `entryToForm` in `CustomCalibreModal`: rich `aliasDetails` win
 * outright, and only a calibre with NO details at all falls back to
 * materialising them from the flat `alsoKnownAs` array. Deliberately not a
 * per-name merge of the two — the editor doesn't do one either, and inventing a
 * second reconciliation rule here is how the two surfaces would drift.
 *
 * Blank names are dropped: they are un-editable rows in the modal and would be
 * written straight back as `{ name: '' }`.
 */
export function effectiveAliasDetails(
  entry: Pick<CalibreAkaPlanInput, 'alsoKnownAs' | 'aliasDetails'>,
): CalibreAliasDetail[] {
  const details = entry.aliasDetails ?? [];
  if (details.length > 0) return details.filter((d) => (d.name || '').trim());
  return (entry.alsoKnownAs ?? [])
    .filter((name) => (name || '').trim())
    .map((name) => ({ name, showInExplore: false }));
}

export interface CalibreAkaBrandPlanRow {
  id: string;
  name: string;
  targetBrandId: string;
  /** The full array to write back — filled entries plus every untouched one. */
  aliasDetails: CalibreAliasDetail[];
  /** How many aliases this row fills. Always > 0 for a row in `writes`. */
  filled: number;
}

export interface CalibreAkaBrandPlan {
  writes: CalibreAkaBrandPlanRow[];
  /** Total aliases filled across every write. */
  aliasesUpdated: number;
  /** Calibres with a target whose aliases are all already scoped (or absent). */
  nothingToDo: CalibreAkaPlanInput[];
  /** Calibres no brand could be derived for. */
  noTarget: CalibreAkaPlanInput[];
}

/**
 * Bucket a selection for the "Set AKA Brands" action.
 *
 * Fill-empty only, per the standing rule for bulk admin ops: an alias that
 * already names a brand — ANY brand, not just the target — is left exactly as
 * it is. The only mutation is adding `brandId` to an alias that has none.
 */
export function planCalibreAkaBrandUpdates(
  entries: CalibreAkaPlanInput[],
  targetBrandIdFor: (calibreId: string) => string | undefined,
): CalibreAkaBrandPlan {
  const writes: CalibreAkaBrandPlanRow[] = [];
  const nothingToDo: CalibreAkaPlanInput[] = [];
  const noTarget: CalibreAkaPlanInput[] = [];

  for (const entry of entries) {
    const targetBrandId = id(targetBrandIdFor(entry.id));
    if (!targetBrandId) {
      noTarget.push(entry);
      continue;
    }
    const aliases = effectiveAliasDetails(entry);
    let filled = 0;
    const next = aliases.map((alias) => {
      if (id(alias.brandId)) return alias;
      filled++;
      return { ...alias, brandId: targetBrandId };
    });
    if (filled === 0) {
      nothingToDo.push(entry);
      continue;
    }
    writes.push({
      id: entry.id,
      name: (entry.name || '').trim() || entry.id,
      targetBrandId,
      aliasDetails: next,
      filled,
    });
  }

  return {
    writes,
    aliasesUpdated: writes.reduce((n, w) => n + w.filled, 0),
    nothingToDo,
    noTarget,
  };
}
